import { useEffect, useState, useRef, useCallback } from "react";
import SvgMapper from "../features/common/SvgMapper";
import UploadEvaluatePage from "./UploadEvaluation/UploadEvaluatePage";
import ReportTable from "./ReportTable";
import { Button } from "@material-tailwind/react";
import { useDispatch, useSelector } from "react-redux";
import { setReportModal, setSelectedInterviewId, setEvaluationMode, clearReportRequestMessage } from "../../store/reportReducer";
import { getAllReports, fetchAllSurveyFeedback } from "../../application/services/reportEvaluationService";

const ReportEvaluationTab = () => {
    const dispatch = useDispatch();
    const { openReportModal, reportRequestMessage  } = useSelector((state) => state.report);
    const [reportsData, setReportsData] = useState([]);
    const [feedbackByInterviewId, setFeedbackByInterviewId] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const hasFetchedInitial = useRef(false);
    const scrollRef = useRef(null);
    const pageSize = 10;

    const fetchReports = useCallback(async (page, append = false) => {
        if (append) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        try {
            const params = {
                page: page,
                pageSize: pageSize
            };
            const response = await getAllReports(params);
            const newGroups = Array.isArray(response.data) ? response.data : [];
            const totalCount = response.total || 0;

            setHasMore((page * pageSize) < totalCount);

            if (append) {
                setReportsData(prev => [...prev, ...newGroups]);
            } else {
                setReportsData(newGroups);
            }
            setCurrentPage(page);
        } catch (error) {
            setError(error.message || "Failed to fetch reports");
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [pageSize]);

    const fetchFeedback = useCallback(async () => {
        try {
            const res = await fetchAllSurveyFeedback();
            const rows = res?.rows || [];
            const map = {};
            rows.forEach((row) => {
                const id = row.interview_id ?? row.interviewId;
                if (id) {
                    map[id] = {
                        rating: row.rating,
                        comment: row.comment,
                        additional_param: row.additional_param,
                    };
                }
            });
            setFeedbackByInterviewId(map);
        } catch (err) {
            console.error("Failed to fetch survey feedback:", err);
        }
    }, []);

    useEffect(() => {
        if (hasFetchedInitial.current) return;
        hasFetchedInitial.current = true;
        fetchReports(1, false);
        fetchFeedback();
    }, [fetchReports, fetchFeedback]);

    const handleLoadMore = useCallback(() => {
        if (!hasMore || loadingMore || loading) return;
        const nextPage = currentPage + 1;
        fetchReports(nextPage, true);
    }, [currentPage, hasMore, loadingMore, loading, fetchReports]);

    useEffect(() => {
        if (!scrollRef.current) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
            if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !loadingMore && !loading) {
                handleLoadMore();
            }
        };

        const container = scrollRef.current;
        container.addEventListener("scroll", handleScroll);

        return () => {
            container.removeEventListener("scroll", handleScroll);
        };
    }, [hasMore, loadingMore, loading, handleLoadMore]);

    const onUpload = (report) => {
        const interviewId = report?.interview_id ?? report?.interviewId;
        if (!report || (interviewId == null) || report.interview_type == null) {
            onExternalUpload();
            return;
        }
        
        const idToUse = interviewId || report.id || report.schedule_id || null;
        dispatch(setSelectedInterviewId(idToUse));
        dispatch(setEvaluationMode("internal"));
        dispatch(setReportModal(true));
    }

    const onExternalUpload = () => {
        dispatch(setSelectedInterviewId(null));
        dispatch(setEvaluationMode("external"));
        dispatch(setReportModal(true));
    }

    useEffect(() => {
        if (!reportRequestMessage) return;

        const timer = setTimeout(() => {
            dispatch(clearReportRequestMessage());
        }, 3000);

        return () => clearTimeout(timer);
    }, [reportRequestMessage, dispatch]);

  return (
    <>
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-semibold text-2xl">Report Evaluation</h2>
          <p className="text-gray-600 mt-1">Upload reports and evaluate them</p>
        </div>
        <div className="flex items-center gap-3">
          <Button className="flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-white"
            onClick={onExternalUpload}>
            {SvgMapper.plus("w-4 h-4")}
            <span>Generate External Report</span>
          </Button>
        </div>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg mt-6">
        <div className="flex items-center gap-2 p-6 border-b border-gray-300">
            <h3 className="font-semibold text-lg text-gray-900">Reports</h3>
            {reportRequestMessage && (
              <p className="mt-1 items-center text-sm text-green-600 font-medium border rounded-lg bg-green-50 border-green-200 p-1">
                {reportRequestMessage}
              </p>
            )}
        </div>
        <div className="p-6">
            <div ref={scrollRef} className="max-h-[600px] overflow-y-auto overflow-x-auto custom-scrollbar pr-2" style={{ scrollBehavior: 'smooth' }}>
                <ReportTable reports={reportsData} feedbackByInterviewId={feedbackByInterviewId} loading={loading} error={error} onAddReport={onUpload} />
                
                {loadingMore && (
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-400"></div>
                    <span className="ml-3 text-gray-600">Loading more reports...</span>
                  </div>
                )}
                
                {!hasMore && Array.isArray(reportsData) && reportsData.length > 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No more reports to load
                  </div>
                )}
            </div>
        </div>
      </div>
      {openReportModal && <UploadEvaluatePage />}
    </>
  );
};

export default ReportEvaluationTab;
